import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Presentation, RefreshCw, Save, CheckCircle2, Play, Volume2, Eye, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import LecturerVideoCard from "@/components/LecturerVideoCard";

const STATUS_COLOR = {
  published: "text-emerald-600 border-emerald-500/40", ready_for_preview: "text-amber-600 border-amber-500/40",
  draft: "text-slate-500 border-slate-400/40", approved: "text-[#0078d4] border-[#0078d4]/40",
};

export default function VirtualLecturer() {
  const [lecturers, setLecturers] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [settings, setSettings] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [studentPreview, setStudentPreview] = useState(null);

  const load = useCallback(async () => {
    const [lec, les, st] = await Promise.all([api.get("/lecturer/lecturers"), api.get("/lecturer/lessons"), api.get("/lecturer/settings")]);
    setLecturers(lec.data.lecturers); setLessons(les.data.lessons); setSettings(st.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openStudio = async (id) => { const { data } = await api.get(`/lecturer/lessons/${id}`); setSel(data); };
  const saveSetting = async (k, v) => { const { data } = await api.put("/lecturer/settings", { ...settings, [k]: v }); setSettings(data); toast.success("Setting saved"); };

  const doGenAudio = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/lecturer/lessons/${sel.id}/audio`); setSel(data); toast.success("Narration audio generated"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Audio generation failed"); } finally { setBusy(false); }
  };
  const doRegen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/lecturer/lessons/generate", {
        id: sel.id, title: sel.title, technology: sel.technology, track: sel.track, role: sel.role,
        scenario_id: sel.scenario_id, objective: sel.objective || sel.title, scenario: sel.scenario || "",
        lecturer_id: sel.lecturer.id });
      setSel(data); toast.success("Script regenerated"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Regeneration failed"); } finally { setBusy(false); }
  };
  const saveScript = async () => { const { data } = await api.put(`/lecturer/lessons/${sel.id}`, { script: sel.script }); setSel(data); toast.success("Draft saved"); await load(); };
  const changeLecturer = async (lid) => { const { data } = await api.put(`/lecturer/lessons/${sel.id}`, { lecturer_id: lid }); setSel(data); await load(); };
  const approve = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/lecturer/lessons/${sel.id}/approve`); setSel(data); toast.success("Approved & published — now visible to students"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); } finally { setBusy(false); }
  };
  const previewAsStudent = async () => {
    if (!sel.has_audio) { await doGenAudio(); }
    const { data } = await api.get(`/lecturer/lessons/${sel.id}`);
    setStudentPreview(data);
  };

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="virtual-lecturer-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-[#0078d4] grid place-items-center text-white"><Presentation className="h-6 w-6" /></div>
        <div>
          <h1 className="font-display font-extrabold text-2xl tracking-tight">Virtual Lecturer Studio</h1>
          <p className="text-sm text-muted-foreground">AI instructor intros — generate, preview & approve before students see them.</p>
        </div>
        {lessons[0] && <Button className="ml-auto gap-2 bg-[#0078d4] text-white" data-testid="preview-new-experience" onClick={() => openStudio(lessons[0].id)}><Eye className="h-4 w-4" /> Preview New Lecturer Experience</Button>}
      </div>

      <Tabs defaultValue="library" className="mt-6">
        <TabsList>
          <TabsTrigger value="library" data-testid="vl-tab-library">Content Library ({lessons.length})</TabsTrigger>
          <TabsTrigger value="settings" data-testid="vl-tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="space-y-2" data-testid="vl-lesson-list">
              {lessons.map((l) => (
                <button key={l.id} onClick={() => openStudio(l.id)} data-testid={`vl-lesson-${l.id}`}
                  className={`w-full text-left rounded-lg border p-3 hover:border-[#0078d4]/50 transition-colors ${sel?.id === l.id ? "border-[#0078d4] bg-[#0078d4]/5" : "border-border"}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{l.title}</span>
                    <Badge variant="outline" className={`ml-auto text-[10px] ${STATUS_COLOR[l.status] || ""}`}>{l.status.replace(/_/g, " ")}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{l.technology} · {l.track} · {l.lecturer.name} · v{l.version}{l.has_audio ? " · audio ✓" : ""}</div>
                </button>
              ))}
            </div>

            {sel && (
              <div className="rounded-xl border border-border p-4 bg-card" data-testid="vl-preview-studio">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs uppercase tracking-widest text-[#0078d4] font-semibold">Preview Studio</span>
                  <Badge variant="outline" className={`ml-auto ${STATUS_COLOR[sel.status] || ""}`}>{sel.status.replace(/_/g, " ")}</Badge>
                </div>
                <div className="text-sm font-semibold">{sel.title}</div>
                <div className="text-xs text-muted-foreground">{sel.technology} · {sel.role} · ~{sel.duration_est}s</div>

                <label className="text-xs text-muted-foreground mt-3 block">Lecturer</label>
                <select value={sel.lecturer.id} onChange={(e) => changeLecturer(e.target.value)} data-testid="vl-lecturer-select"
                  className="w-full mt-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm">
                  {lecturers.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.gender}, {l.voice})</option>)}
                </select>

                <label className="text-xs text-muted-foreground mt-3 block">Narration script</label>
                <Textarea value={sel.script} onChange={(e) => setSel({ ...sel, script: e.target.value })} rows={6} className="mt-1 text-sm" data-testid="vl-script" />

                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={saveScript} className="gap-1" data-testid="vl-save"><Save className="h-4 w-4" />Save Draft</Button>
                  <Button size="sm" variant="outline" onClick={doRegen} disabled={busy} className="gap-1" data-testid="vl-regen"><RefreshCw className="h-4 w-4" />Regenerate</Button>
                  <Button size="sm" variant="outline" onClick={doGenAudio} disabled={busy} className="gap-1" data-testid="vl-genaudio"><Volume2 className="h-4 w-4" />{busy ? "Working…" : "Generate Audio"}</Button>
                  <Button size="sm" variant="outline" onClick={previewAsStudent} className="gap-1" data-testid="vl-preview-student"><Play className="h-4 w-4" />Preview as Student</Button>
                  <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white ml-auto" onClick={approve} disabled={busy} data-testid="vl-approve"><CheckCircle2 className="h-4 w-4" />Approve &amp; Publish</Button>
                </div>

                {sel.has_audio && (
                  <div className="mt-4"><LecturerVideoCard lesson={sel} autoplay="require" allowSkip={false} /></div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          {settings && (
            <div className="rounded-xl border border-border p-5 bg-card max-w-xl space-y-4" data-testid="vl-settings">
              {[["enabled", "Enable Virtual Lecturer"], ["allow_skip", "Allow students to skip"], ["require_captions", "Require captions"], ["enable_transcripts", "Enable transcripts"], ["enable_recap", "Enable recap videos"], ["low_bandwidth_default", "Low-bandwidth mode by default"], ["approval_required", "Approval required before publishing"], ["playback_speed_controls", "Playback-speed controls"]].map(([k, label]) => (
                <label key={k} className="flex items-center justify-between text-sm" data-testid={`vl-set-${k}`}>
                  <span>{label}</span>
                  <Switch checked={!!settings[k]} onCheckedChange={(v) => saveSetting(k, v)} />
                </label>
              ))}
              <div className="flex items-center justify-between text-sm">
                <span>Lecturer selection</span>
                <select value={settings.selection_mode} onChange={(e) => saveSetting("selection_mode", e.target.value)} data-testid="vl-set-mode" className="rounded-md border border-input bg-background px-2 py-1 text-sm">
                  <option value="random">Random</option><option value="rotation">Rotation</option><option value="specific">Specific</option>
                </select>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Gender filter</span>
                <select value={settings.gender_filter} onChange={(e) => saveSetting("gender_filter", e.target.value)} data-testid="vl-set-gender" className="rounded-md border border-input bg-background px-2 py-1 text-sm">
                  <option value="any">Any</option><option value="male">Male only</option><option value="female">Female only</option>
                </select>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Autoplay</span>
                <select value={settings.autoplay} onChange={(e) => saveSetting("autoplay", e.target.value)} data-testid="vl-set-autoplay" className="rounded-md border border-input bg-background px-2 py-1 text-sm">
                  <option value="muted">Autoplay muted</option><option value="require">Require Play</option><option value="audio">Auto-start audio</option>
                </select>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Preview as Student modal */}
      {studentPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" data-testid="student-preview-modal" onClick={() => setStudentPreview(null)}>
          <div className="bg-background rounded-xl max-w-lg w-full p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs uppercase tracking-widest text-[#0078d4] font-semibold">Preview as Student</span>
              <button onClick={() => setStudentPreview(null)} className="ml-auto text-muted-foreground" data-testid="student-preview-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="text-xs text-muted-foreground mb-2">Production scenario: {studentPreview.scenario || studentPreview.title}</div>
            <LecturerVideoCard lesson={studentPreview} autoplay="muted"
              continueLabel="Start Hands-On Task"
              onContinue={() => { toast.success("(Preview) → would open the guided lab"); setStudentPreview(null); }} />
          </div>
        </div>
      )}
    </div>
  );
}
